import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const ApiDocumentation = ({ isOpen, onClose }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && !content) {
      setLoading(true);
      // Загружаем документацию при открытии модального окна
      import('../docs/ARTISAN_BEAZLEY.md.js')
        .then(module => {
          setContent(module.default);
        })
        .catch(error => {
          console.error('Error loading documentation:', error);
          setContent('# Error loading documentation\nUnable to load the API documentation.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, content]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop show"
      style={{
        display: 'block',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1040,
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl"
        style={{
          maxWidth: '90%',
          width: '90%',
          minHeight: '90vh',
          margin: '5vh auto',
          display: 'flex',
          alignItems: 'stretch'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content"
          style={{
            width: '100%',
            minHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
          }}
        >
          <div className="modal-header" style={{ borderBottom: '1px solid #dee2e6', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 className="modal-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500' }}>
              Artisan Beazley API Documentation
            </h4>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onClose}
              style={{
                minWidth: 'auto',
                padding: '4px 8px',
                marginLeft: 'auto',
                flexShrink: 0
              }}
            >
              ✕
            </button>
          </div>
          <div
            className="modal-body"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              maxHeight: 'calc(90vh - 120px)' // Вычитаем высоту header и footer
            }}
          >
            {loading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p>Loading documentation...</p>
              </div>
            ) : (
              <div className="documentation-content" style={{ lineHeight: '1.6' }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    // Стилизация таблиц
                    table: ({ children }) => (
                      <div className="table-responsive mb-4">
                        <table className="table table-striped table-bordered table-hover">
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className="table-dark">
                        {children}
                      </thead>
                    ),
                    th: ({ children, ...props }) => (
                      <th {...props} style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>
                        {children}
                      </th>
                    ),
                    td: ({ children, ...props }) => (
                      <td {...props} style={{ padding: '8px', fontSize: '14px' }}>
                        {children}
                      </td>
                    ),
                    // Стилизация кода
                    code: ({ node, inline, className, children, ...props }) => {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline ? (
                        <pre className="bg-dark text-light p-3 rounded mb-3" style={{ fontSize: '13px', overflow: 'auto' }}>
                          <code className={className} {...props}>
                            {children}
                          </code>
                        </pre>
                      ) : (
                        <code className="bg-light px-2 py-1 rounded text-danger" style={{ fontSize: '13px', fontFamily: 'monospace' }} {...props}>
                          {children}
                        </code>
                      );
                    },
                    // Стилизация ссылок
                    a: ({ children, href, ...props }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary" {...props}>
                        {children}
                      </a>
                    ),
                    // Стилизация заголовков
                    h1: ({ children }) => (
                      <h1 className="text-primary border-bottom pb-2 mb-4" style={{ fontSize: '2rem', fontWeight: '600' }}>
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-secondary mt-5 mb-3" style={{ fontSize: '1.5rem', fontWeight: '500' }}>
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-dark mt-4 mb-2" style={{ fontSize: '1.25rem', fontWeight: '500' }}>
                        {children}
                      </h3>
                    ),
                    h4: ({ children }) => (
                      <h4 className="text-dark mt-3 mb-2" style={{ fontSize: '1.1rem', fontWeight: '500' }}>
                        {children}
                      </h4>
                    ),
                    // Стилизация blockquote
                    blockquote: ({ children }) => (
                      <blockquote className="blockquote border-start border-primary border-4 bg-light p-3 mb-3">
                        {children}
                      </blockquote>
                    ),
                    // Стилизация списков
                    ul: ({ children }) => (
                      <ul className="mb-3" style={{ paddingLeft: '20px' }}>
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="mb-3" style={{ paddingLeft: '20px' }}>
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="mb-1">
                        {children}
                      </li>
                    ),
                    // Стилизация HR
                    hr: () => (
                      <hr className="my-4" style={{ border: '0', height: '2px', background: 'linear-gradient(to right, #007bff, transparent)' }} />
                    ),
                    // Поддержка HTML тегов details и summary
                    details: ({ children }) => (
                      <details className="mb-3 border rounded p-2 bg-light">
                        {children}
                      </details>
                    ),
                    summary: ({ children }) => (
                      <summary
                        className="fw-bold text-primary"
                        style={{ cursor: 'pointer', padding: '8px', borderRadius: '4px', backgroundColor: '#f8f9fa' }}
                      >
                        {children}
                      </summary>
                    )
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ borderTop: '1px solid #dee2e6', padding: '15px 20px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default ApiDocumentation;